import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  motion, AnimatePresence, useScroll, useTransform,
  useMotionValue, useSpring, useMotionTemplate,
} from "framer-motion";
import {
  Play, X, ArrowRight, Film, Camera, Sparkles, Users, Layers, Clapperboard,
  Wand2, Globe,
} from "lucide-react";
import { HERO, SECTION_ART, SHOWCASE, CATEGORIES } from "@/data/showcase";
import { LiveVideo } from "@/components/LiveVideo";
import { PRODUCTIONS, STORIES, SHOWREEL as SHOWREEL_FILM, FILM_SCIFI } from "@/data/videos";

const usePRM = () => {
  const [prm, setPrm] = useState(false);
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrm(m.matches);
    const h = (e) => setPrm(e.matches);
    m.addEventListener?.("change", h);
    return () => m.removeEventListener?.("change", h);
  }, []);
  return prm;
};

/* Cinematic springs — slow, damped: reads as a ~2s camera settle, not a CSS trick */
const CAM = { stiffness: 42, damping: 17, mass: 1.1 };
const PUSH = { stiffness: 34, damping: 22, mass: 1 };

const NAV = [
  { label: "Product", to: "#product" },
  { label: "Showcase", to: "#showcase" },
  { label: "How It Works", to: "#how" },
  { label: "Pricing", to: "#pricing" },
];

/* Floating dust motes — same lightweight ambient system as the studio login */
function Motes({ prm, count = 18 }) {
  if (prm) return null;
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {Array.from({ length: count }).map((_, i) => {
        const size = 1 + (i % 3);
        const left = (i * 41) % 100;
        const top = (i * 59) % 100;
        const dur = 11 + (i % 6) * 2.4;
        return (
          <motion.span
            key={i}
            className="absolute rounded-full bg-white/35"
            style={{ width: size, height: size, left: `${left}%`, top: `${top}%` }}
            animate={{ y: [0, -30, 0], opacity: [0.12, 0.55, 0.12] }}
            transition={{ duration: dur, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
          />
        );
      })}
    </div>
  );
}

/* ---------- video/still slot: poster-first, plays MP4 only if provided ---------- */
function MediaSlot({ item, className, autoplayOnHover = false, priority = false, ambient = false, prm = false }) {
  const ref = useRef(null);
  const [hover, setHover] = useState(false);
  const hasVideo = !!item.video;
  useEffect(() => {
    const v = ref.current;
    if (!v || !hasVideo) return;
    if (hover) v.play?.().catch(() => {});
    else { v.pause?.(); v.currentTime = 0; }
  }, [hover, hasVideo]);
  const drift = ambient && !prm;
  const Frame = drift ? motion.div : "div";
  const frameProps = drift
    ? { initial: { scale: 1.05 }, animate: { scale: [1.05, 1.13] }, transition: { duration: 30, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" } }
    : {};
  return (
    <div
      className={"relative overflow-hidden bg-[#0a0b13] " + (className || "")}
      onMouseEnter={() => autoplayOnHover && setHover(true)}
      onMouseLeave={() => autoplayOnHover && setHover(false)}
    >
      <Frame className="absolute inset-0" {...frameProps}>
        <img
          src={item.poster} alt={item.title}
          loading={priority ? "eager" : "lazy"} decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: hover && hasVideo ? 0 : 1, transition: "opacity .4s ease" }}
        />
        {hasVideo && (
          <video
            ref={ref} src={item.video} poster={item.poster}
            muted loop playsInline preload="none"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
      </Frame>
    </div>
  );
}

/* ---------- section wrapper with scroll reveal ---------- */
function Reveal({ children, className, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.2, 0.7, 0.2, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ==================================================================
   HOLOGRAPHIC SHOWCASE CARD
   Layered depth planes over the original (untouched) artwork:
     PLANE 0  image           — recedes against cursor + slow push-in
     PLANE 1  atmosphere      — violet haze drifting independently
     PLANE 2  glass surface   — hairline key light, corner highlight,
                                cursor-tracked lighting, specular sweep,
                                gradient holo border
     PLANE 3  foreground      — chips + title floating at translateZ
   Card body lifts toward the viewer; max tilt ~3.4deg.
   All motion disabled under prefers-reduced-motion / on touch.
   ================================================================== */
function ShowcaseCard({ item, onOpen, prm, idx }) {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const hv = useMotionValue(0);

  const rotateX = useSpring(useTransform(my, [0, 1], [3.4, -3.4]), CAM);
  const rotateY = useSpring(useTransform(mx, [0, 1], [-3.4, 3.4]), CAM);
  const lift = useSpring(useTransform(hv, [0, 1], [0, 16]), CAM);

  const imgX = useSpring(useTransform(mx, [0, 1], [10, -10]), CAM);
  const imgY = useSpring(useTransform(my, [0, 1], [7, -7]), CAM);
  const imgScale = useSpring(useTransform(hv, [0, 1], [1.05, 1.13]), PUSH);

  const hazeX = useSpring(useTransform(mx, [0, 1], [-6, 6]), CAM);
  const hazeY = useSpring(useTransform(my, [0, 1], [-4, 4]), CAM);

  const fgX = useSpring(useTransform(mx, [0, 1], [-5, 5]), CAM);
  const fgY = useSpring(useTransform(my, [0, 1], [-3.5, 3.5]), CAM);

  const lightO = useSpring(useTransform(hv, [0, 1], [0, 1]), PUSH);
  const sx = useTransform(mx, (v) => v * 100);
  const sy = useTransform(my, (v) => v * 100);
  const sheen = useMotionTemplate`radial-gradient(520px circle at ${sx}% ${sy}%, rgba(196,188,255,0.10), transparent 60%)`;

  const onMove = (e) => {
    if (prm) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => { mx.set(0.5); my.set(0.5); hv.set(0); };

  return (
    <motion.div
      initial={prm ? { opacity: 0 } : { opacity: 0, y: 26, scale: 0.97, rotateX: 5 }}
      whileInView={prm ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1, rotateX: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (idx % 3) * 0.08, ease: [0.2, 0.7, 0.2, 1] }}
      style={{ perspective: 1200 }}
    >
      <motion.button
        onClick={() => onOpen(item)}
        onMouseMove={onMove}
        onMouseEnter={() => !prm && hv.set(1)}
        onMouseLeave={onLeave}
        data-testid={`showcase-card-${item.slug}`}
        aria-label={`Open ${item.title} presentation`}
        style={prm ? undefined : { rotateX, rotateY, z: lift, transformStyle: "preserve-3d" }}
        className="group relative block w-full text-left will-change-transform"
      >
        {/* media frame — morphs into the fullscreen viewer (shared layoutId) */}
        <motion.div
          layoutId={`sc-${item.id}`}
          transition={{ layout: { duration: 0.6, ease: [0.2, 0.7, 0.2, 1] } }}
          className="relative aspect-video overflow-hidden rounded-xl bg-[#0a0b13] ring-1 ring-white/10 group-hover:ring-white/5 transition-shadow duration-500 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.7)] group-hover:shadow-[0_34px_70px_-18px_rgba(0,0,0,0.85),0_0_60px_-16px_rgba(91,77,255,0.4)]"
        >
          {/* PLANE 0 — artwork */}
          <motion.img
            src={item.thumbnail} alt={item.title}
            loading="lazy" decoding="async"
            style={prm ? { scale: 1.04 } : { x: imgX, y: imgY, scale: imgScale }}
            className="absolute inset-0 w-full h-full object-cover will-change-transform"
          />
          {/* PLANE 1 — atmospheric depth, drifts independently of the artwork */}
          {!prm && (
            <motion.div
              style={{ x: hazeX, y: hazeY }}
              className="absolute inset-[-8%] pointer-events-none bg-[radial-gradient(60%_50%_at_30%_25%,rgba(91,77,255,0.10),transparent_65%)]"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
          {/* PLANE 2 — glass: hairline key light + corner highlight */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-[12%] right-[12%] h-px bg-[linear-gradient(90deg,transparent,rgba(124,112,255,0.5),transparent)] opacity-60" />
            <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.07),transparent_28%)]" />
          </div>
          {/* cursor-tracked dimensional lighting */}
          {!prm && <motion.div className="absolute inset-0 pointer-events-none" style={{ background: sheen, opacity: lightO }} />}
          {/* single specular sweep per hover */}
          {!prm && <div className="vyom-card-sweep" aria-hidden />}
          {/* holographic gradient border */}
          <div
            className="absolute inset-0 rounded-xl p-px opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
            style={{
              background: "linear-gradient(135deg, rgba(140,124,255,0.55), rgba(255,255,255,0.05) 35%, rgba(255,179,0,0.22))",
              WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
              WebkitMaskComposite: "xor", maskComposite: "exclude",
            }}
          />
          {/* play affordance */}
          <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-white/10 border border-white/30 backdrop-blur"><Play className="w-5 h-5 text-white translate-x-0.5" /></span>
          </div>
        </motion.div>
        {/* PLANE 3 — foreground floats toward the viewer */}
        <motion.div
          style={prm ? undefined : { x: fgX, y: fgY, z: 44 }}
          className="absolute inset-0 pointer-events-none"
        >
          <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
            <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-md bg-black/60 text-[#c9c1ff] border border-white/10 backdrop-blur">{item.category}</span>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-3.5">
            <div className="text-white font-display font-semibold leading-tight" style={{ textShadow: "0 1px 10px rgba(0,0,0,0.6)" }}>{item.title}</div>
            <div className="text-[11px] text-slate-300/90 mt-0.5 line-clamp-1">{item.description}</div>
          </div>
        </motion.div>
      </motion.button>
    </motion.div>
  );
}

/* ---------- cinematic fullscreen viewer — shared-layout morph from the card ---------- */
function Viewer({ item, onClose, prm }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.25 } }}
      className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
      onClick={onClose} data-testid="showcase-viewer"
    >
      <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <motion.div
          layoutId={`sc-${item.id}`}
          transition={{ layout: { duration: 0.6, ease: [0.2, 0.7, 0.2, 1] } }}
          className="relative rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-[0_60px_140px_-30px_rgba(0,0,0,0.9),0_0_90px_-20px_rgba(91,77,255,0.35)]"
        >
          <MediaSlot item={{ ...item, video: item.video }} className="aspect-video" priority ambient={!prm} prm={prm} />
          {/* hairline key light — same treatment as studio panels */}
          <div className="absolute top-0 left-[10%] right-[10%] h-px bg-[linear-gradient(90deg,transparent,rgba(124,112,255,0.6),transparent)] pointer-events-none" />
          {!item.video && (
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <span className="text-[11px] font-mono uppercase tracking-widest text-white/70 bg-black/50 px-3 py-1.5 rounded-full border border-white/15">Vyom Production Concept</span>
            </div>
          )}
          <button onClick={onClose} data-testid="viewer-close" autoFocus aria-label="Close viewer" className="absolute top-3 right-3 grid place-items-center w-10 h-10 rounded-full bg-black/60 border border-white/15 text-white hover:bg-black/80"><X className="w-5 h-5" /></button>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 0.25, duration: 0.5, ease: [0.2, 0.7, 0.2, 1] } }}
          exit={{ opacity: 0, transition: { duration: 0.15 } }}
          className="flex flex-wrap items-start justify-between gap-3 mt-4"
        >
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-md bg-[#5B4DFF]/20 text-[#c9c1ff] border border-[#5B4DFF]/30">{item.category}</span>
              {item.collection === "bonus-animation" && (
                <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-md bg-[#FFB300]/15 text-[#ffd166] border border-[#FFB300]/30">Bonus · Stylized Animation</span>
              )}
              {item.tags.map((t) => <span key={t} className="text-[10px] font-mono uppercase tracking-wider text-slate-400">· {t}</span>)}
            </div>
            <h3 className="font-display text-2xl font-semibold text-white mt-2">{item.title}</h3>
            <p className="text-sm text-slate-300 mt-1 max-w-xl">{item.description}</p>
          </div>
          <Link to="/login" data-testid="viewer-create-cta"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#5B4DFF] hover:bg-[#4a3ff0] text-white text-sm font-semibold whitespace-nowrap transition shadow-[0_0_28px_rgba(91,77,255,0.45)]">
            Create something like this <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </motion.div>
  );
}

function FeatureRow({ eyebrow, title, body, art, reverse, testid }) {
  return (
    <Reveal className={"grid lg:grid-cols-2 gap-8 lg:gap-14 items-center " + (reverse ? "lg:[direction:rtl]" : "")}>
      <div className="lg:[direction:ltr]">
        <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#8c7cff] mb-3">{eyebrow}</div>
        <h3 className="font-display text-3xl sm:text-4xl font-semibold text-white leading-[1.08]" data-testid={testid}>{title}</h3>
        <p className="text-base text-slate-300/90 mt-4 max-w-lg leading-relaxed">{body}</p>
      </div>
      <div className="lg:[direction:ltr] group rounded-2xl overflow-hidden ring-1 ring-white/10 relative shadow-[0_24px_60px_-30px_rgba(0,0,0,0.9)]">
        <img src={art} alt={title} loading="lazy" decoding="async" className="w-full aspect-video object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
        <div className="absolute top-0 left-[12%] right-[12%] h-px bg-[linear-gradient(90deg,transparent,rgba(124,112,255,0.5),transparent)] opacity-60 pointer-events-none" />
      </div>
    </Reveal>
  );
}

const PACKS = [
  { name: "Starter", price: "₹199", credits: "1,600", note: "For first productions" },
  { name: "Creator", price: "₹499", credits: "4,000", note: "For regular creators" },
  { name: "Pro", price: "₹999", credits: "8,000", note: "Watermark-free exports", popular: true },
  { name: "Studio", price: "₹1,999", credits: "16,000", note: "For teams & studios" },
  { name: "Production", price: "₹4,999", credits: "40,000", note: "High-volume output" },
];

export default function Welcome() {
  const prm = usePRM();
  const [cat, setCat] = useState("ALL");
  const [open, setOpen] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, prm ? 1 : 1.15]);
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", prm ? "0%" : "18%"]);

  /* hero depth: 3 parallax planes — artwork recedes, atmosphere drifts
     independently, typography floats — all on slow cinematic springs */
  const hmx = useMotionValue(0.5);
  const hmy = useMotionValue(0.5);
  const HERO_CAM = { stiffness: 28, damping: 19, mass: 1.3 };
  const heroImgX = useSpring(useTransform(hmx, [0, 1], [14, -14]), HERO_CAM);
  const heroImgY = useSpring(useTransform(hmy, [0, 1], [10, -10]), HERO_CAM);
  const heroHazeX = useSpring(useTransform(hmx, [0, 1], [-7, 7]), HERO_CAM);
  const heroHazeY = useSpring(useTransform(hmy, [0, 1], [-5, 5]), HERO_CAM);
  const heroTxtX = useSpring(useTransform(hmx, [0, 1], [-5, 5]), HERO_CAM);
  const heroTxtY = useSpring(useTransform(hmy, [0, 1], [-3, 3]), HERO_CAM);

  const onHeroMove = (e) => {
    if (prm) return;
    const r = e.currentTarget.getBoundingClientRect();
    hmx.set((e.clientX - r.left) / r.width);
    hmy.set((e.clientY - r.top) / r.height);
  };
  const onHeroLeave = () => { hmx.set(0.5); hmy.set(0.5); };

  useEffect(() => {
    document.title = "Vyom — Turn Ideas Into Productions | AI Production Intelligence";
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const items = useMemo(() => cat === "ALL" ? SHOWCASE : SHOWCASE.filter((s) => s.category === cat), [cat]);

  return (
    <div className="min-h-screen bg-[#06070d] text-white overflow-x-hidden" data-testid="welcome-page">
      <div className="vyom-grain" aria-hidden />
      <div className="relative z-10">
      {/* NAV */}
      <header className={"fixed top-0 inset-x-0 z-50 transition-colors duration-300 " + (scrolled ? "bg-[#06070d]/85 backdrop-blur-xl border-b border-white/8" : "bg-transparent")}>
        <div className="max-w-[1280px] mx-auto px-5 h-16 flex items-center justify-between">
          <a href="#top" data-testid="brand-logo"><img src="/brand/logo-white.png" alt="Vyom" className="h-6 w-auto" /></a>
          <nav className="hidden md:flex items-center gap-7">
            {NAV.map((n) => <a key={n.label} href={n.to} className="text-sm text-slate-300 hover:text-white transition">{n.label}</a>)}
          </nav>
          <div className="flex items-center gap-2.5">
            <Link to="/login" className="text-sm text-slate-300 hover:text-white px-3 py-2" data-testid="nav-login">Log in</Link>
            <Link to="/login" data-testid="nav-create" className="text-sm font-semibold px-4 py-2 rounded-lg bg-white text-[#06070d] hover:bg-slate-200 transition">Create</Link>
          </div>
        </div>
      </header>

      {/* HERO — layered cinematic depth: artwork / atmosphere / typography planes */}
      <section ref={heroRef} id="top" onMouseMove={onHeroMove} onMouseLeave={onHeroLeave} className="relative h-[100svh] min-h-[620px] flex items-end overflow-hidden">
        <motion.div style={{ scale: heroScale, y: heroY }} className="absolute inset-0">
          {/* PLANE 0 — artwork (slow push-in) */}
          <motion.div style={prm ? undefined : { x: heroImgX, y: heroImgY }} className="absolute inset-0 will-change-transform">
            <motion.div
              className="absolute inset-[-3%] will-change-transform"
              initial={{ scale: 1.06 }}
              animate={prm ? { scale: 1 } : { scale: [1.06, 1.13] }}
              transition={prm ? { duration: 0 } : { duration: 26, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
            >
              <picture>
                <source media="(max-width: 640px)" srcSet={HERO.posterMobile} />
                <img src={HERO.poster} alt="Vyom cinematic hero" className="w-full h-full object-cover" fetchPriority="high" />
              </picture>
              {HERO.video && <video src={HERO.video} poster={HERO.poster} autoPlay={!prm} muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />}
            </motion.div>
            {/* diagonal projector light sweep — room light, not painted on the artwork */}
            {!prm && <div className="vyom-sweep" aria-hidden />}
          </motion.div>
          {/* PLANE 1 — atmosphere (drifts independently of the artwork) */}
          {!prm && (
            <motion.div style={{ x: heroHazeX, y: heroHazeY }} className="absolute inset-0 will-change-transform">
              <div className="cine-haze opacity-70" />
            </motion.div>
          )}
          {/* volumetric violet key-light — same glow language as the studio */}
          {!prm && <div className="cine-orb left-[6%] top-[28%] w-[440px] h-[440px] bg-[#5B4DFF]/20" aria-hidden />}
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#06070d] via-[#06070d]/30 to-[#06070d]/60" />
        <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_20%,transparent_40%,rgba(6,7,13,0.55)_100%)]" />
        <Motes prm={prm} />
        {/* PLANE 2 — typography floats toward the viewer */}
        <motion.div style={prm ? undefined : { x: heroTxtX, y: heroTxtY }} className="relative max-w-[1280px] mx-auto px-5 pb-20 sm:pb-28 w-full will-change-transform">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}>
            <div className="text-[12px] font-mono uppercase tracking-[0.3em] text-[#8c7cff] mb-4">Vyom · AI Production Intelligence</div>
            <h1 className="font-display font-bold leading-[0.95] text-5xl sm:text-7xl lg:text-[5.5rem] max-w-4xl" style={{ textShadow: "0 2px 30px rgba(0,0,0,0.6)" }}>
              TURN IDEAS<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-[#c9c1ff] to-[#8c7cff]">INTO PRODUCTIONS.</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-300 mt-6 max-w-xl leading-relaxed">
              AI-powered production intelligence for cinematic stories, films, campaigns and content.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link to="/login" data-testid="hero-start" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#5B4DFF] hover:bg-[#4a3ff0] text-white font-semibold transition shadow-[0_0_40px_rgba(91,77,255,0.5)]">
                START CREATING <ArrowRight className="w-4.5 h-4.5" />
              </Link>
              <a href="#showreel" data-testid="hero-watch" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white/8 border border-white/15 text-white font-semibold hover:bg-white/12 backdrop-blur transition">
                <Play className="w-4.5 h-4.5" /> WATCH VYOM IN ACTION
              </a>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* MADE WITH VYOM — REAL cinematic productions (dominant + supporting) */}
      <section id="made" className="relative bg-[#070810] border-b border-white/8 py-24 sm:py-32 overflow-hidden">
        {!prm && (
          <>
            <div className="cine-orb left-[-8%] top-[6%] w-[520px] h-[520px] bg-[#5B4DFF]/12" aria-hidden />
            <div className="cine-orb right-[-10%] bottom-[2%] w-[460px] h-[460px] bg-[#2ea6ff]/8" style={{ animationDelay: "-13s" }} aria-hidden />
          </>
        )}
        <div className="relative max-w-[1280px] mx-auto px-5">
          <Reveal className="max-w-2xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#8c7cff] mb-3">Made with Vyom</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold leading-[1.04]" data-testid="section-made">CINEMA, BUILT DIFFERENTLY.</h2>
            <p className="text-sm sm:text-base text-slate-400 mt-3 max-w-xl">Real footage from the Vyom universe. Move your cursor over any frame to hear it — the film keeps playing.</p>
          </Reveal>
          <Reveal className="grid lg:grid-cols-12 gap-4 sm:gap-5 mt-10" delay={0.08}>
            {/* dominant */}
            <LiveVideo item={PRODUCTIONS[0]} testid="made-dominant" priority
              className="lg:col-span-8 aspect-video ring-1 ring-white/10 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.9)]" />
            {/* supporting stack */}
            <div className="lg:col-span-4 grid grid-rows-2 gap-4 sm:gap-5">
              <LiveVideo item={PRODUCTIONS[1]} testid="made-support-1" className="aspect-video ring-1 ring-white/10" />
              <LiveVideo item={PRODUCTIONS[2]} testid="made-support-2" className="aspect-video ring-1 ring-white/10" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* MADE WITH / EXPLORE VYOM STRIP */}
      <section id="product" className="border-y border-white/8 bg-[#080910]">
        <div className="max-w-[1280px] mx-auto px-5 py-6 flex items-center gap-x-8 gap-y-3 flex-wrap justify-center text-slate-400">
          <span className="text-[11px] font-mono uppercase tracking-[0.24em]">Explore Vyom</span>
          {["Cinematic Production", "Camera Intelligence", "Character Continuity", "VFX & Worldbuilding", "Production Intelligence"].map((t) => (
            <span key={t} className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">{t}</span>
          ))}
        </div>
      </section>

      {/* DON'T GENERATE CLIPS */}
      <section className="max-w-[1280px] mx-auto px-5 py-24 sm:py-32">
        <Reveal className="text-center max-w-3xl mx-auto">
          <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#8c7cff] mb-4">The Vyom difference</div>
          <h2 className="font-display text-4xl sm:text-6xl font-bold leading-[1.02]" data-testid="section-productions">
            DON'T GENERATE CLIPS.<br /><span className="text-slate-400">BUILD PRODUCTIONS.</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-300/90 mt-6 leading-relaxed">
            Vyom orchestrates the whole film — story, script, scenes, shots, characters, camera, voice, music and continuity — so every frame belongs to one coherent production, not a pile of disconnected clips.
          </p>
        </Reveal>
        <Reveal className="grid sm:grid-cols-3 gap-4 mt-14" delay={0.1}>
          {[
            { icon: Film, t: "Story → Shots", d: "A director-grade plan breaks your idea into scenes and purposeful shots." },
            { icon: Users, t: "Characters that persist", d: "Character memory keeps faces, wardrobe and identity consistent across the film." },
            { icon: Layers, t: "One coherent world", d: "Visual direction, audio and continuity are managed as a single production." },
          ].map((c) => (
            <div key={c.t} className="rounded-2xl p-6 bg-white/[0.03] ring-1 ring-white/10">
              <c.icon className="w-6 h-6 text-[#8c7cff]" />
              <div className="font-display font-semibold text-white mt-4">{c.t}</div>
              <div className="text-sm text-slate-400 mt-1.5 leading-relaxed">{c.d}</div>
            </div>
          ))}
        </Reveal>
      </section>

      {/* FEATURE ROWS */}
      <section className="max-w-[1280px] mx-auto px-5 space-y-24 sm:space-y-32 pb-24 sm:pb-32">
        <FeatureRow testid="section-camera" eyebrow="Camera Intelligence" title="EVERY SHOT HAS A PURPOSE." body="Vyom designs deliberate camera language — establishing frames, dollies, orbits and reveals — so motion serves the story instead of drifting at random." art={SECTION_ART.camera} />
        <FeatureRow testid="section-vfx" reverse eyebrow="VFX & Worldbuilding" title="CREATE WORLDS THAT DON'T EXIST." body="From enchanted forests to vertical megacities, Vyom builds believable environments with atmosphere, scale and controlled effects." art={SECTION_ART.vfx} />
        <FeatureRow testid="section-character" eyebrow="Character Continuity" title="CHARACTERS THAT STAY YOURS." body="A character defined once stays recognizable across every scene — the same face, the same wardrobe, the same presence." art={SECTION_ART.character} />
        <FeatureRow testid="section-nova" reverse eyebrow="Meet Nova" title="YOUR PRODUCTION INTELLIGENCE." body="Nova is the creative brain of your studio — it plans, tracks continuity, explains decisions and keeps your production on course from idea to final cut." art={SECTION_ART.production} />
      </section>

      {/* CINEMATIC STORIES — REAL vertical reels with layered depth */}
      <section id="stories" className="relative bg-[#080910] border-y border-white/8 py-24 sm:py-28 overflow-hidden">
        {!prm && <div className="cine-orb left-[40%] top-[10%] w-[440px] h-[440px] bg-[#7C70FF]/10" aria-hidden />}
        <div className="relative max-w-[1280px] mx-auto px-5">
          <Reveal className="max-w-2xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#8c7cff] mb-3">Cinematic Stories</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold leading-[1.04]">VERTICAL. SOCIAL. STILL CINEMATIC.</h2>
            <p className="text-sm sm:text-base text-slate-400 mt-3 max-w-xl">Short-form reels that carry the same production craft as the big screen.</p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mt-10">
            {STORIES.map((s, i) => (
              <motion.div
                key={s.slug}
                initial={prm ? { opacity: 0 } : { opacity: 0, y: 30 }}
                whileInView={prm ? { opacity: 1 } : { opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: (i % 4) * 0.09, ease: [0.2, 0.7, 0.2, 1] }}
                className="will-change-transform"
                style={prm ? undefined : { marginTop: i % 2 === 1 ? 28 : 0 }}
              >
                <LiveVideo item={s} testid={`story-${s.slug}`} rounded="rounded-2xl"
                  className="aspect-[9/16] ring-1 ring-white/10 shadow-[0_28px_60px_-28px_rgba(0,0,0,0.9)] hover:ring-[#5B4DFF]/50 transition-shadow duration-500" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOWCASE GALLERY — holographic card system over ambient orbs */}
      <section id="showcase" className="relative bg-[#080910] border-y border-white/8 py-24 sm:py-28 overflow-hidden">
        {!prm && (
          <>
            <div className="cine-orb left-[-6%] top-[12%] w-[480px] h-[480px] bg-[#5B4DFF]/12" aria-hidden />
            <div className="cine-orb right-[-8%] bottom-[6%] w-[420px] h-[420px] bg-[#FFB300]/8" style={{ animationDelay: "-11s" }} aria-hidden />
          </>
        )}
        <div className="relative max-w-[1280px] mx-auto px-5">
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#8c7cff] mb-3">The Concept Gallery</div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold">The range of worlds Vyom can build</h2>
              <p className="text-sm text-slate-400 mt-3 max-w-2xl">Production concepts across genres — hover any frame for cinematic depth.</p>
            </div>
          </Reveal>
          <div className="flex flex-wrap gap-2 mt-8" data-testid="showcase-filters">
            {CATEGORIES.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`filter-${c}`}
                className={"text-xs font-mono uppercase tracking-wider px-3.5 py-1.5 rounded-full border transition " + (cat === c ? "bg-[#5B4DFF] border-[#5B4DFF] text-white" : "bg-white/[0.03] border-white/10 text-slate-400 hover:text-white hover:border-white/25")}>
                {c}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8" data-testid="showcase-grid">
            {items.map((it, idx) => <ShowcaseCard key={it.id} item={it} onOpen={setOpen} prm={prm} idx={idx} />)}
          </div>
        </div>
      </section>

      {/* ONE IDEA -> SEQUENCE (HOW IT WORKS) */}
      <section id="how" className="max-w-[1280px] mx-auto px-5 py-24 sm:py-32">
        <Reveal className="text-center max-w-3xl mx-auto">
          <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#8c7cff] mb-4">How it works</div>
          <h2 className="font-display text-4xl sm:text-6xl font-bold leading-[1.02]" data-testid="section-sequence">ONE IDEA.<br />AN ENTIRE SEQUENCE.</h2>
        </Reveal>
        <Reveal className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-14" delay={0.1}>
          {[
            { i: Sparkles, t: "Idea" }, { i: Clapperboard, t: "Story & Script" }, { i: Users, t: "Characters" },
            { i: Camera, t: "Scenes & Shots" }, { i: Wand2, t: "Camera & VFX" }, { i: Film, t: "Voice & Audio" },
            { i: Layers, t: "Continuity" }, { i: Globe, t: "Final Production" },
          ].map((s, idx) => (
            <div key={s.t} className="rounded-xl p-5 bg-white/[0.03] ring-1 ring-white/10 flex items-center gap-3">
              <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#5B4DFF]/15 border border-[#5B4DFF]/30 text-[#c9c1ff] shrink-0"><s.i className="w-4.5 h-4.5" /></span>
              <div><div className="text-[10px] font-mono text-slate-500">STEP {idx + 1}</div><div className="text-sm font-medium text-white">{s.t}</div></div>
            </div>
          ))}
        </Reveal>
      </section>

      {/* USE CASES */}
      <section className="bg-[#080910] border-y border-white/8 py-20">
        <div className="max-w-[1280px] mx-auto px-5">
          <Reveal><h2 className="font-display text-3xl sm:text-4xl font-bold text-center">Built for every kind of production</h2></Reveal>
          <Reveal className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-10" delay={0.1}>
            {["Films & Shorts", "Ad Campaigns", "Music & Performance", "Social Content", "Trailers", "Explainers", "Documentary", "Brand Films"].map((u) => (
              <div key={u} className="rounded-xl px-5 py-6 bg-white/[0.03] ring-1 ring-white/10 text-center">
                <div className="font-display font-semibold text-white">{u}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-[1280px] mx-auto px-5 py-24 sm:py-28">
        <Reveal className="text-center max-w-2xl mx-auto">
          <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#8c7cff] mb-4">Pricing</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold">Simple credit packs</h2>
          <p className="text-sm text-slate-400 mt-3">Buy the credits you need. No free trials, no subscriptions to cancel — credits never expire.</p>
        </Reveal>
        <Reveal className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-12" delay={0.1}>
          {PACKS.map((p) => (
            <div key={p.name} data-testid={`pack-${p.name.toLowerCase()}`}
              className={"rounded-2xl p-6 ring-1 relative " + (p.popular ? "bg-[#5B4DFF]/10 ring-[#5B4DFF]/50" : "bg-white/[0.03] ring-white/10")}>
              {p.popular && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-mono uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-[#5B4DFF] text-white">Most popular</span>}
              <div className="font-display font-semibold text-white">{p.name}</div>
              <div className="text-3xl font-bold mt-2">{p.price}</div>
              <div className="text-sm text-[#FFB300] font-mono mt-1">{p.credits}</div>
              <div className="text-xs text-slate-400 mt-3">{p.note}</div>
              <Link to="/login" className={"mt-5 block text-center text-sm font-semibold py-2.5 rounded-xl transition " + (p.popular ? "bg-[#5B4DFF] hover:bg-[#4a3ff0] text-white" : "bg-white/8 hover:bg-white/12 text-white border border-white/12")}>Get started</Link>
            </div>
          ))}
        </Reveal>
      </section>

      {/* SHOWREEL — REAL cinematic film, presented like a premiere */}
      <section id="showreel" className="relative max-w-[1280px] mx-auto px-5 pb-24 sm:pb-32 pt-4">
        <Reveal className="text-center max-w-2xl mx-auto mb-8">
          <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#8c7cff] mb-3">The Screening Room</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold">The Vyom Showreel</h2>
        </Reveal>
        <Reveal className="relative rounded-3xl overflow-hidden ring-1 ring-white/12 shadow-[0_60px_140px_-40px_rgba(0,0,0,0.95),0_0_120px_-30px_rgba(91,77,255,0.28)]">
          <LiveVideo item={SHOWREEL_FILM} testid="showreel-video" rounded="rounded-3xl" showMeta={false}
            className="aspect-video" />
          <div className="pointer-events-none absolute bottom-5 left-0 right-0 text-center">
            <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-white/70">Hover to listen · {SHOWREEL_FILM.label}</div>
          </div>
        </Reveal>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-28 sm:py-36 text-center overflow-hidden">
        <img src={SECTION_ART.future} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-30" loading="lazy" />
        <div className="absolute inset-0 bg-[#06070d]/70" />
        <Reveal className="relative max-w-3xl mx-auto px-5">
          <h2 className="font-display text-4xl sm:text-6xl font-bold leading-[1.02]" data-testid="final-cta-heading">YOUR STORY.<br />YOUR WORLD.<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-[#8c7cff]">YOUR PRODUCTION.</span></h2>
          <Link to="/login" data-testid="final-cta" className="inline-flex items-center gap-2 mt-8 px-7 py-4 rounded-xl bg-[#5B4DFF] hover:bg-[#4a3ff0] text-white font-semibold transition shadow-[0_0_50px_rgba(91,77,255,0.5)]">
            START CREATING <ArrowRight className="w-5 h-5" />
          </Link>
        </Reveal>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/8 bg-[#080910]">
        <div className="max-w-[1280px] mx-auto px-5 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex flex-col items-center sm:items-start">
            <img src="/brand/logo-white.png" alt="Vyom" className="h-7 w-auto" />
            <div className="text-xs text-slate-500 mt-2">AI production intelligence for cinematic stories.</div>
          </div>
          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-400" data-testid="footer-legal">
            <Link to="/login" className="hover:text-white">Product</Link>
            <a href="#showcase" className="hover:text-white">Showcase</a>
            <a href="#pricing" className="hover:text-white">Pricing</a>
            <a href="mailto:hello@vyomluv.com" className="hover:text-white">Contact</a>
            <a href="/terms" className="hover:text-white">Terms</a>
            <a href="/privacy" className="hover:text-white">Privacy</a>
            <a href="/refund-policy" className="hover:text-white">Refunds</a>
          </nav>
        </div>
        <div className="text-center text-[11px] text-slate-600 pb-8">© {new Date().getFullYear()} Vyom. Showcase frames are Vyom production concepts.</div>
      </footer>
      </div>

      <AnimatePresence>
        {open && <Viewer item={open} onClose={() => setOpen(null)} prm={prm} />}
      </AnimatePresence>
    </div>
  );
}
